<script lang="ts" setup>
import { useTaskStore } from "@core/stores/task.store"

import { computedEager } from "@vueuse/core"
import { computed } from "vue"

const taskStore = useTaskStore()

type Props = {
    activeStep: number
    value: number
    tasks?: { step: string; message: string; status: string }[]
    vertical?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    vertical: false,
    tasks: () => [],
})

const getAnalyseTask = computedEager(() => {
    return taskStore.getTaskByTaskname("get_compare_graph_by_building_ids")
})

const STATE = {
    WAITING: "waiting",
    PROCESSING: "processing",
    FINISHED: "finished",
    FAILURE: "failure",
}

function getProgressBarBySubTask(sub_taskname: string) {
    // Cherche dans les progress_tasks si la tâche est terminée
    const task = getAnalyseTask.value?.progress_tasks.find(
        (task) => task.sub_taskname === sub_taskname
    )

    const nsteps = task?.nsteps
    const step = task?.step

    if (!nsteps) {
        return null
    }

    return {
        value: step,
        max: nsteps,
        valuePercent: ((step ?? 0) / nsteps) * 100,
    }
}

const getTasksList = computed(() => {
    return props.tasks?.map((task) => {
        return {
            ...task,
            progress_bar: getProgressBarBySubTask(task.step),
        }
    })
})

function getTaskIconByStatus(status: string) {
    switch (status) {
        case STATE.PROCESSING:
            return "pi pi-spin pi-spinner"
        case STATE.FINISHED:
            return "pi pi-check"
        case STATE.FAILURE:
            return "pi pi-times"
        default:
            return "pi pi-pause"
    }
}

function getStepColorByStatus(status: string) {
    switch (status) {
        case STATE.PROCESSING:
            return "var(--p-primary)"
        case STATE.FINISHED:
            return "var(--p-teal-500)"
        case STATE.FAILURE:
            return "var(--p-red-500)"
        default:
            return ""
    }
}
</script>

<template>
    <BaseStepPanel :value="value">
        <div v-if="tasks?.length" class="dashboard-card-compare-task-item">
            <div
                v-for="(task, indexSubTasks) in getTasksList"
                :key="indexSubTasks"
                class="dashboard-card-compare-task-item__content"
            >
                <div class="dashboard-card-compare-task-item__header">
                    <BaseIcon
                        v-if="task.status !== STATE.WAITING"
                        :icon="getTaskIconByStatus(task.status)"
                        :color="getStepColorByStatus(task.status)"
                    />

                    <p
                        :class="
                            task.status === STATE.WAITING
                                ? 'dashboard-card-compare-task-item__inactive'
                                : ''
                        "
                    >
                        {{ task.message }}
                    </p>
                </div>
                <!-- Barre de progression -->
                <div
                    v-if="task.progress_bar"
                    class="dashboard-card-compare-task-item__progress-bar"
                >
                    <BaseProgressBar
                        :value="task.progress_bar?.valuePercent"
                        class="w-full"
                    >
                        <span class="text-xs">
                            Période traitée :
                            {{ task.progress_bar?.value }}
                            /
                            {{ task.progress_bar?.max }}
                        </span>
                    </BaseProgressBar>
                    <span
                        v-if="!task.progress_bar?.value"
                        class="text-xs text-primary dashboard-card-compare-task-item__progress-bar-empty-text"
                    >
                        Période traitée :
                        {{ task.progress_bar?.value }}
                        /
                        {{ task.progress_bar?.max }}
                    </span>
                </div>
            </div>
        </div>
    </BaseStepPanel>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.dashboard-card-compare-task-item {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    border-radius: 0.375rem;
    border: 1px solid #e0e0e0;
    background: #fff;
    padding: 1.5rem;

    .dashboard-card-compare-task-item__content {
        display: flex;
        gap: 1rem;

        align-items: center;

        --p-progressbar-height: 1rem;

        @media (--medium-viewport) {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
        }
    }

    .dashboard-card-compare-task-item__header {
        display: flex;
        gap: 1rem;
        align-items: center;
        width: 100%;
        & > p {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        @media (--medium-viewport) {
            gap: 0.5rem;
        }
    }

    .dashboard-card-compare-task-item__inactive {
        padding-left: 2rem;
        color: #9e9e9e;
    }

    .dashboard-card-compare-task-item__progress-bar {
        position: relative;
        width: 100%;

        .dashboard-card-compare-task-item__progress-bar-empty-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }
}
</style>
